// Shared rendering for "weekly hard sets per muscle vs MEV/MAV/MRV" — used both by History
// (logged volume, rolling average) and by the program-coverage views (planned volume). Owns the
// zone bar, the per-muscle row, the one-line coaching summary, and the educational "(i)" modal, so
// the landmarks visualisation has a single source of truth. Higher-level entry points:
//   • volumeBreakdown(items, opts) — summary + rows from `[{ name, perWeek }]`
//   • programCoverage(days, opts)  — full body for a program schedule (resolves muscle names)

import { h } from "../core/dom.js";
import { icon } from "../lib/icons.js";
import { openModal } from "./ui.js";
import { muscleName } from "../store.js";
import { landmarksFor, setZone, VOLUME_GLOSSARY } from "../lib/landmarks.js";
import { planVolume } from "../lib/programVolume.js";

// A track whose background paints the four volume zones (below-MEV → MEV–MAV → MAV–MRV →
// above-MRV) with a marker at the current weekly sets, so the landmarks are visible.
export function volumeZoneBar(perWeek, lm, zone) {
  const scale = Math.max(lm.mrv * 1.25, perWeek * 1.05, 1);
  const p = (v) => Math.min(100, Math.max(0, (v / scale) * 100));
  const grad =
    `linear-gradient(90deg, var(--warn) 0 ${p(lm.mev)}%, var(--success) ${p(lm.mev)}% ${p(lm.mav)}%,` +
    ` #eab308 ${p(lm.mav)}% ${p(lm.mrv)}%, var(--danger) ${p(lm.mrv)}% 100%)`;
  return h("div", { style: { position: "relative", height: "14px", borderRadius: "7px", background: "var(--surface-2)" }, title: `MEV ${lm.mev} · MAV ${lm.mav} · MRV ${lm.mrv} hard sets/wk` },
    h("span", { style: { position: "absolute", inset: "0", borderRadius: "7px", background: grad, opacity: ".4" } }),
    h("span", { style: { position: "absolute", left: `${p(perWeek)}%`, top: "-3px", bottom: "-3px", width: "3px", marginLeft: "-1.5px", background: zone.color, borderRadius: "2px", boxShadow: "0 0 0 2px var(--card)" } })
  );
}

// One muscle's row: name · sets/wk · zone label, the zone bar, and a nudge when actionable.
// `muteNudge` drops the per-row coaching line (used in a light week, where repeating "add a few
// sets" under every muscle reads as scolding — the summary line covers it once).
export function volumeRow({ name, perWeek, lm, zone, muteNudge }) {
  const actionable = !muteNudge && (zone.key === "below" || zone.key === "over");
  return h("div", { class: "mv-row", style: { padding: "8px 0" } },
    h("div", { class: "row-between", style: { fontSize: ".84rem", marginBottom: "6px" } },
      h("span", { style: { fontWeight: 600 } }, name),
      h("span", { class: "dim" },
        h("b", { style: { color: zone.color } }, `${perWeek}`), "/wk · ",
        h("span", { style: { color: zone.color, fontWeight: 600 } }, zone.label))
    ),
    volumeZoneBar(perWeek, lm, zone),
    actionable ? h("div", { class: "dim", style: { fontSize: ".74rem", marginTop: "5px", color: zone.color } }, zone.nudge) : null
  );
}

// One-line coaching takeaway above the per-muscle rows. `planned` swaps wording from "you did" to
// "this plan does" for the prospective (program) views.
export function coverageSummary(below, over, { planned = false, lightWeek = false } = {}) {
  // A light stretch (most muscles down at once) is time off or a deload, not eleven separate
  // programming errors — one neutral line instead of a wall of per-muscle deficits.
  if (lightWeek) {
    return h("div", { class: "hstack", style: { gap: "6px", alignItems: "flex-start", margin: "0 0 10px", fontSize: ".8rem", color: "var(--text-2)", fontWeight: 600 } },
      icon("info", { size: 14 }), h("span", null, "Volume was down across most muscles — a light stretch or time off. Ease back in; the landmarks below will catch up."));
  }
  const parts = [];
  if (below.length) parts.push(`${below.length} below minimum volume (MEV)`);
  if (over.length) parts.push(`${over.length} above recoverable volume (MRV)`);
  const balanced = planned
    ? "Well balanced — this plan keeps most muscles in their growth zone."
    : "Nicely balanced — most muscles are in their growth zone.";
  const text = parts.length ? `${parts.join(" · ")} — see the notes below.` : balanced;
  const color = parts.length ? "var(--warn)" : "var(--success)";
  return h("div", { class: "hstack", style: { gap: "6px", alignItems: "flex-start", margin: "0 0 10px", fontSize: ".8rem", color, fontWeight: 600 } },
    icon(parts.length ? "info" : "check", { size: 14 }), h("span", null, text));
}

// Summary + per-muscle rows from `[{ name, perWeek }]`. Each name is resolved to its landmark
// (case-insensitively) and zone. Rows keep the caller's order (callers sort by volume desc).
export function volumeBreakdown(items, opts = {}) {
  const entries = items.map(({ name, perWeek }) => {
    const lm = landmarksFor(name);
    return { name, perWeek, lm, zone: setZone(perWeek, lm) };
  });
  const below = entries.filter((e) => e.zone.key === "below");
  const over = entries.filter((e) => e.zone.key === "over");
  // "Light week" detection — only for LOGGED volume (a plan that under-doses most muscles is a
  // real programming problem and keeps the full per-muscle callout).
  const lightWeek = !opts.planned && below.length >= 5 && below.length > entries.length / 2;
  if (lightWeek) for (const e of below) e.muteNudge = true;
  return h("div", null, coverageSummary(below, over, { ...opts, lightWeek }), ...entries.map(volumeRow));
}

// The educational core: defines hard set + MEV/MAV/MRV in plain language.
export function openVolumeInfo() {
  openModal({
    title: "Understanding training volume",
    content: h("div", null,
      h("p", { class: "dim", style: { fontSize: ".88rem", lineHeight: "1.5", marginTop: 0 } },
        "This counts ", h("b", null, "hard sets"), " per muscle and compares them to research-based volume landmarks, so you can see where to add work and where to ease off."),
      h("div", null, ...VOLUME_GLOSSARY.map(([term, desc]) =>
        h("div", { style: { padding: "9px 0", borderTop: "1px solid var(--border)" } },
          h("div", { style: { fontWeight: 700, fontSize: ".88rem" } }, term),
          h("div", { class: "dim", style: { fontSize: ".84rem", lineHeight: "1.45", marginTop: "2px" } }, desc)))),
      h("p", { class: "dim", style: { fontSize: ".76rem", lineHeight: "1.45", marginTop: "12px" } },
        "Landmarks are rough population averages and vary by person, exercise, and recovery — a guide, not a rule. Secondary muscles (e.g. triceps on a bench press) count as half a set. Source: Renaissance Periodization volume landmarks.")
    ),
  });
}

// Full coverage body for a program schedule: projected weekly hard sets per muscle if every
// workout is done as planned. `days` is the normalized schedule (`[{ exercises }]`, rest days
// empty); `cycleDays` defaults to the schedule length. Only muscles the plan actually trains are
// shown. Returns a fragment (intro note + breakdown, or an empty hint).
export function programCoverage(days, { cycleDays } = {}) {
  const cyc = cycleDays ?? days.length;
  const { muscles } = planVolume(days, cyc);
  const items = muscles
    .filter((m) => m.sets_per_week > 0)
    .map((m) => ({ name: muscleName(m.muscle_id) || "—", perWeek: m.sets_per_week }));

  if (!items.length) {
    return h("p", { class: "dim", style: { fontSize: ".84rem", margin: "4px 0" } },
      "Add workout days with resistance exercises to see the weekly hard sets each muscle would get.");
  }

  const weekly = cyc === 7;
  const note = weekly
    ? "Weekly hard sets each muscle would get if you complete every workout as planned."
    : `Hard sets per muscle if you complete every workout, scaled from your ${cyc}-day cycle to a 7-day week.`;
  return h("div", null,
    h("p", { class: "dim", style: { fontSize: ".76rem", margin: "0 0 8px", lineHeight: "1.45" } },
      `${note} The coloured band shows each muscle's growth zone (MEV → MAV → MRV).`),
    volumeBreakdown(items, { planned: true })
  );
}
